import {
  AnyAction,
  CombinedState,
  combineReducers,
  configureStore as configureRTKStore,
  ConfigureStoreOptions,
  createListenerMiddleware,
  Reducer,
  ReducersMapObject,
} from '@reduxjs/toolkit';
import { ManagedStore } from '../definitions/types';

export function createReducerManager(
  initialReducers: ConfigureStoreOptions['reducer'],
) {
  let combinedReducer: Reducer;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let reducers: ReducersMapObject<any, AnyAction> = {};

  if (typeof initialReducers === 'function') {
    combinedReducer = initialReducers;
  } else {
    reducers = { ...initialReducers };

    // Create the initial combinedReducer
    combinedReducer = combineReducers(reducers);
  }

  // An array which is used to delete state keys when reducers are removed
  let keysToRemove: string[] = [];

  return {
    getReducerMap: () => reducers,

    // The root reducer function exposed by this object
    // This will be passed to the store
    reduce: (
      state: CombinedState<{ [key: string]: unknown }> | undefined,
      action: AnyAction,
    ) => {
      // If any reducers have been removed, clean up their state first
      if (keysToRemove.length > 0) {
        state = { ...state };
        for (const key of keysToRemove) {
          delete state[key];
        }
        keysToRemove = [];
      }

      // Delegate to the combined reducer
      return combinedReducer(state, action);
    },

    // Adds a new reducer with the specified key
    add: (key: string, reducer: Reducer) => {
      if (!key || reducers[key]) {
        return;
      }

      // Add the reducer to the reducer mapping
      reducers[key] = reducer;

      // Generate a new combined reducer
      combinedReducer = combineReducers(reducers);
    },

    // Removes a reducer with the specified key
    remove: (key: string) => {
      if (!key || !reducers[key]) {
        return;
      }

      // Remove it from the reducer mapping
      delete reducers[key];

      // Add the key to the list of keys to clean up
      keysToRemove.push(key);

      // Generate a new combined reducer
      combinedReducer = combineReducers(reducers);
    },
  };
}

export function configureStore(options: ConfigureStoreOptions): ManagedStore {
  const reducerManager = createReducerManager(options.reducer);
  const listenerMiddleware = createListenerMiddleware();

  // Create a store with the root reducer function being the one exposed by the manager.
  const store = configureRTKStore({
    ...options,
    reducer: reducerManager.reduce,
    middleware(getDefaultMiddleware) {
      return getDefaultMiddleware().prepend(listenerMiddleware.middleware);
    },
  }) as ManagedStore;

  store.reducerManager = reducerManager;

  return store;
}
